<script setup lang="ts">
    import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
    import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter, type RouteLocationNormalized } from 'vue-router';
    import { http } from '@/shared/httpWrapper';
    import Breadcrumbs from '@/shared/components/Breadcrumbs.vue';
    import { getCodeSetViewOrderBy, type CodeSetViewResultCodeRow } from '@/shared/helpers/api';
    import { createCodeDefArray, type CodeDef } from '@/shared/helpers/dataHelpers';
    import { selectedColumnsStorageKey } from './CodeGrid.helper';
    import CodesetDisplayOptions from './CodesetDisplayOptions.vue';
    import CodeSetHeader from './CodeSetHeader.vue';
    import CodeGrid from './CodeGrid.vue';
    import CodeEdit from '@/shared/codeedit/CodeEdit.vue';
    import { askToLeaveEditor } from '@/shared/codeedit/askToLeaveEditor';
    import useBuefy from '@/shared/composables/useBuefy';
    import GovernanceInformation from '@/shared/components/GovernanceInformation.vue';
    import SplitPane from '@/shared/components/SplitPane.vue';
    import { canEditCode } from '@/shared/directives/requirePermission';
    import type { CodeSetProp, Library } from '@/shared/helpers/api/types/entities';

    const props = defineProps<{
        codeSet: CodeSetProp;
    }>();

    const emit = defineEmits<{
        rowcount: [count: number];
        refresh: [];
    }>();

    const route = useRoute();
    const router = useRouter();

    // Read once, so that later URL updates do not re-apply state to the grid.
    const initialUrlState = {
        filter: typeof route.query.filter === 'string' ? route.query.filter : undefined,
        group: typeof route.query.group === 'string' ? route.query.group : undefined,
        sort: typeof route.query.sort === 'string' ? route.query.sort : undefined
    };

    const tableColumns = ref<CodeDef[]>([]);
    const tableRows = ref<CodeSetViewResultCodeRow[]>([]);
    const loading = ref(true);
    const library = ref<Library | null>(null);
    const gridQueries = ref<string[][]>([]);
    const exportCols = ref<string[] | null>(null);
    const libraryHeaderOpen = ref(true);
    const userCanEditCodes = ref(false);
    const editCodeId = ref<string | null>(null);
    const editorOpen = ref(false);
    const codeEditLoaded = ref(false);
    const gridRef = useTemplateRef('grid');
    const codeEditRef = useTemplateRef('codeEdit');
    const buefy = useBuefy();
    const gridDisplayedCount = ref(0);

    // What the grid is showing. The grid is only mounted while there are codes to show, so an empty
    // code set has to read as zero rather than keeping the last count the grid reported before it
    // went away: otherwise deleting the last code leaves a stale count in the header, and hides the
    // "No Items Found" message that is the only thing left on the page.
    const displayedCodeCount = computed(() => tableRows.value.length === 0 ? 0 : gridDisplayedCount.value);

    const nameAlias = computed(() => library.value ? library.value.nameAlias : '');

    // The filters the API is asked to apply for export, publish and lookup URLs. They mirror
    // exactly what the grid is showing, so the counts and the results always agree.
    // `regEx` is part of the API's filter contract but the grid has no free-text search, so it
    // is always empty here.
    const queries = computed(() => ({
        regEx: [] as string[],
        queries: gridQueries.value
    }));

    onMounted(async () => {
        library.value = props.codeSet.library;
        await load();
    });

    async function load() {
        loading.value = true;
        await loadCodes();

        userCanEditCodes.value = await canEditCode({
            libraryName: library.value!.name,
            scopes: props.codeSet.scopes
        });

        tableColumns.value = createCodeDefArray(library.value!.attributeDefinitions);
        emit('rowcount', tableRows.value.length);

        libraryHeaderOpen.value = !route.query.noheader;

        loading.value = false;
    }

    async function reloadDataAfterChange() {
        loading.value = true;
        await loadCodes();

        emit('rowcount', tableRows.value.length);

        loading.value = false;
    }

    // The grid owns column visibility. The selection is mirrored to the legacy key because the
    // lookup URL and the code table export are both built from it.
    function onVisibleColumns(fields: string[]) {
        localStorage.setItem(selectedColumnsStorageKey(library.value!.name), fields.join(','));

        exportCols.value = fields.map((field) => {
            switch (field) {
                case 'name': return 'Name';
                case 'description': return 'Description';
                case 'identity': return 'Identity';
                case 'isValid': return 'IsValid';
                case 'dateUpdated': return 'DateUpdated';
                case 'dateCreated': return 'DateCreated';
                case 'attachmentKey': return 'AttachmentKey';
                case 'commonLibraryIRI': return 'CommonLibraryIRI';
                default: return field;
            }
        });
    }

    function changeCodeRefDisplayMode(callback: (_cols: CodeDef[]) => void) {
        callback(tableColumns.value);
        localStorage.setItem('Library.' + library.value!.name + '.ReferenceDisplayOverrides', tableColumns.value.filter(c => c.type.toUpperCase() === 'CODEREF').map(c => `${c.field}:${c.referenceDisplayMode}`).toString());
    }

    async function loadCodes() {
        tableRows.value = (await getCodeSetViewOrderBy(http, props.codeSet.name, 'Name')).data.node.codeRow;
    }

    // The editor does not reload itself when it is pointed at another item, and the pane now stays
    // open from one edit to the next, so it is rebuilt whenever the item changes.
    watch(editCodeId, () => {
        codeEditLoaded.value = false;
    });

    // Closing the editor drops whatever it was pointing at, so that reopening it starts a new item
    // rather than the one that was last edited.
    watch(editorOpen, (open) => {
        if (!open) {
            editCodeId.value = null;
            codeEditLoaded.value = false;
        }
    });

    // Keeps the address bar in step with the grid so the page can be bookmarked or shared.
    // `replace` is used so that filtering does not fill up the browser history.
    function updateUrl(gridState: { filter?: string; group?: string; sort?: string }) {
        const query: Record<string, string> = {};

        for (const [key, value] of Object.entries(route.query)) {
            if (!['filter', 'group', 'sort'].includes(key) && typeof value === 'string')
                query[key] = value;
        }

        if (gridState.filter) query.filter = gridState.filter;
        if (gridState.group) query.group = gridState.group;
        if (gridState.sort) query.sort = gridState.sort;

        if (JSON.stringify(query) === JSON.stringify(route.query))
            return;

        router.replace({ query });
    }

    // Pointing the editor at another item rebuilds it, which would silently drop what has not been
    // saved for the item it shows now, so that is asked first.
    function onEditItemRequest(item: { id: string }) {
        if (editorOpen.value && editCodeId.value === item.id)
            return;

        const openItem = () => {
            editCodeId.value = item.id;
            editorOpen.value = true;
        };

        if (hasUnsavedEdits())
            askToLeaveEditor(buefy, openItem);
        else
            openItem();
    }

    function hasUnsavedEdits(): boolean {
        return editorOpen.value && !!codeEditRef.value?.isDirty;
    }

    // Leaving the page drops the editor too, whether through a link in the grid or anywhere else.
    // The grid keeps its filter, grouping and sorting in the query, so a change to the query alone
    // stays on the page and is let through without asking.
    function confirmLeavingPage(to: RouteLocationNormalized, from: RouteLocationNormalized): boolean | Promise<boolean> {
        if (to.path === from.path || !hasUnsavedEdits())
            return true;

        return new Promise(resolve => askToLeaveEditor(buefy, () => resolve(true), () => resolve(false)));
    }

    onBeforeRouteLeave(confirmLeavingPage);
    onBeforeRouteUpdate(confirmLeavingPage);

    async function onEditorRefresh() {
        editorOpen.value = false;
        editCodeId.value = null;
        await reloadDataAfterChange();
        emit('refresh');
    }
</script>

<template>
    <div class="h-100 is-flex is-flex-direction-column">
        <div class="is-flex">
            <breadcrumbs
                class="mr-3"
                :code-set="codeSet"
                :library="library" />
            <b-button
                class="ml-auto has-margin-top has-margin-bottom is-text"
                :icon-right="libraryHeaderOpen ? 'menu-up' : 'menu-down'"
                @click="libraryHeaderOpen = !libraryHeaderOpen">
                {{ libraryHeaderOpen ? "Hide library header" : "Show library header" }}
            </b-button>
        </div>
        <b-collapse
            class="has-margin-bottom"
            :model-value="libraryHeaderOpen">
            <div>
                <code-set-header
                    v-model:editor-active="editorOpen"
                    :code-set="codeSet"
                    :filtered-code-count="displayedCodeCount"
                    :code-count="tableRows.length"
                    :export-columns="exportCols"
                    :queries="queries"
                    @refresh="onEditorRefresh" />
                <div
                    class="is-flex is-gap-10 mt-3 is-flex-wrap-wrap-reverse is-justify-content-flex-end">
                    <codeset-display-options
                        v-if="!loading"
                        :library="library"
                        @coderef-display-mode="changeCodeRefDisplayMode($event)" />

                    <b-button
                        v-if="!loading"
                        icon-left="table-column-width"
                        title="Clear column filters and restore the default columns, order, widths and sorting"
                        @click="gridRef?.resetColumns()">
                        Reset columns
                    </b-button>
                </div>
            </div>
        </b-collapse>
        <div class="is-flex-grow-1">
            <b-loading
                v-model="loading"
                :is-full-page="false"
                :can-cancel="false" />
            <split-pane
                :open="editorOpen && !!library"
                :storage-key="library ? `Library.${library.name}.CodeEditorWidth` : null"
                label="code editor">
                <template #primary>
                    <code-grid
                        v-if="tableRows.length > 0"
                        ref="grid"
                        :rows="tableRows"
                        :columns="tableColumns"
                        :library-name="library?.name ?? ''"
                        :name-alias="nameAlias"
                        :edit-button-enabled="userCanEditCodes"
                        :url-state="initialUrlState"
                        @edit="onEditItemRequest"
                        @visible-columns="onVisibleColumns"
                        @displayed-count="gridDisplayedCount = $event"
                        @filters="gridQueries = $event"
                        @url-state="updateUrl" />
                    <div v-else-if="tableRows.length === 0 && !loading">
                        <section class="section has-margin-top2">
                            <div class="content has-text-grey has-text-centered title is-5">
                                <p>No Items Found</p>
                            </div>
                        </section>
                    </div>
                </template>

                <template #secondary>
                    <governance-information
                        v-show="codeEditLoaded"
                        :library-name="library!.name" />
                    <code-edit
                        ref="codeEdit"
                        :key="editCodeId ?? 'new'"
                        :library="library!.name"
                        :scopes="codeSet.scopes"
                        :code-set-name="codeSet.name"
                        :code-id="editCodeId ?? undefined"
                        @refresh="onEditorRefresh"
                        @cancel="editorOpen = false"
                        @loaded="codeEditLoaded = true" />
                </template>
            </split-pane>
        </div>
    </div>
</template>
